if (!customElements.get('careiz-upsells')) {
  customElements.define('careiz-upsells', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.config = JSON.parse(this.querySelector('[data-careiz-config]').textContent);
      this.entries = [];
      this.instance = crypto.randomUUID();
      this.cartQueue = Promise.resolve();
      this.content = this.querySelector('[data-careiz-content]');
      this.status = this.querySelector('[data-careiz-status]');
      this.start();
    }

    async start() {
      try {
        if (this.config.context === 'cart') return await this.startCart();
        this.rule = this.config.rules.find(rule => rule.type === 'product_rule' && Number(rule.triggerId) === Number(this.config.productId));
        this.settings = {...this.config};
        for (const key of ['design','mode','heading']) {
          if (this.rule?.[key] && this.rule[key] !== 'inherit') this.settings[key] = this.rule[key];
        }
        this.form = document.querySelector(`product-form form[action*="/cart/add"] [name="id"][value="${this.mainVariant()?.id}"]`)?.form;
        if (!this.form) this.form = document.querySelector('product-form form[action*="/cart/add"]');
        if (!this.form || this.form.closest('quick-add-modal')) return;
        this.dataset.productForm = this.form.getAttribute('id');
        document.querySelectorAll('careiz-upsells[data-product-form]').forEach(element => {
          if (element !== this && element.dataset.productForm === this.dataset.productForm) element.remove();
        });
        this.productInfo = this.form.closest('[id^="ProductInfo-"]') || this.form.closest('section');
        const slot = this.productInfo?.querySelector('[data-careiz-upsell-slot]');
        if (slot) slot.append(this);
        else this.form.closest('product-form').before(this);
        const seen = new Set();
        this.entries = this.config.rules.filter(rule => rule.type === 'upsell' && (!rule.triggerId || Number(rule.triggerId) === Number(this.config.productId))).flatMap(rule => {
          const product = rule.product;
          if (!product || Number(product.id) === Number(this.config.productId) || seen.has(product.id)) return [];
          const variants = product.variants.filter(variant => variant.available);
          if (!variants.length) return [];
          const value = Number(rule.discount.value);
          const discounted = rule.discount.type !== 'none' && value > 0;
          const valid = Number.isFinite(value) && value >= 0 && (rule.discount.type !== 'percentage' || value <= 100);
          const configured = valid && (!discounted || ((rule.discount.type !== 'fixed_amount' || this.config.currency === 'ILS') && typeof rule.discount.code === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(rule.discount.code)));
          if (!configured && !this.config.editor) return [];
          seen.add(product.id);
          return [{product,variants,variant:variants[0],offer:rule,configured,selected:false,added:0,busy:false,token:`${this.instance}-${product.id}`}];
        });
        if (!this.entries.length) return this.emptyEditor();
        this.classList.add(`careiz-upsells--${this.settings.design}`, `careiz-upsells--${this.settings.mode}`);
        this.style.setProperty('--cu-accent',this.settings.accent);
        this.style.setProperty('--cu-image',`${this.settings.imageSize}px`);
        this.render();
        this.productInfo?.classList.add('careiz-upsells-ready');
        this.hidden = !this.entries.some(entry => this.matchesVariant(entry.offer,this.mainVariant()?.id));
        this.changeListener = event => { if (!this.contains(event.target)) this.refreshMain(); };
        document.addEventListener('change',this.changeListener);
        this.mainObserver = new MutationObserver(mutations => {
          if (mutations.some(mutation => !this.contains(mutation.target))) this.refreshMain();
        });
        this.mainObserver.observe(this.productInfo, {subtree:true, childList:true, attributes:true, attributeFilter:['value']});
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.cartUnsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate,() => {
            this.getCart().then(cart => {
              if (!this.isConnected) return;
              for (const entry of this.entries) {
                if (entry.busy) continue;
                const line = cart.items.find(item => item.properties?._careiz_offer_key === entry.token);
                if (line) {entry.added = line.quantity;entry.actualPrice = Math.round(line.final_line_price/line.quantity);}
                else if (entry.added) {entry.added = 0;entry.actualPrice = undefined;entry.selected = false;}
                this.updateEntry(entry);
              }
            }).catch(() => {});
          });
        }
      } catch (error) {
        console.warn('Careiz upsells could not load.',error);
      }
    }

    disconnectedCallback() {
      queueMicrotask(() => {
        if (this.isConnected) return;
        this.mainObserver?.disconnect();
        this.cartUnsubscribe?.();
        document.removeEventListener('change',this.changeListener);
        this.productInfo?.classList.remove('careiz-upsells-ready');
      });
    }

    root() { return `${this.config.root.replace(/\/$/,'')}/`; }
    async startCart() {
      this.settings = {...this.config};
      this.classList.add('careiz-upsells--cart',`careiz-upsells--${this.settings.design}`,'careiz-upsells--button');
      this.style.setProperty('--cu-accent',this.settings.accent);
      this.style.setProperty('--cu-image',`${this.settings.imageSize}px`);
      this.cartRevision = 0;
      const initialCart = await this.getCart();
      if (!this.isConnected) return;
      this.syncCart(initialCart);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cartUnsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
          const revision = ++this.cartRevision;
          this.getCart().then(cart => {
            if (this.isConnected && revision === this.cartRevision) this.syncCart(cart);
          }).catch(() => {});
        });
      }
    }

    syncCart(cart) {
      this.status.textContent = '';
      const items = cart.items || [];
      this.preview = Boolean(this.config.editor && !items.length && this.config.previewProductId);
      const mains = items.filter(item => item.properties?._careiz_upsell !== 'true');
      const mainIds = this.preview ? [Number(this.config.previewProductId)] : mains.map(item => Number(item.product_id));
      const seen = new Set();
      const previous = new Map(this.entries.map(entry => [Number(entry.product.id),entry]));
      this.entries = this.config.rules.filter(rule => rule.type === 'upsell').flatMap(rule => {
        const triggerId = Number(rule.triggerId) || mainIds[0];
        const product = rule.product;
        if (!mainIds.includes(triggerId) || !product || mainIds.includes(Number(product.id)) || seen.has(Number(product.id))) return [];
        if (!this.preview && !mains.some(item => Number(item.product_id) === triggerId && this.matchesVariant(rule,item.variant_id || item.id))) return [];
        const lines = items.filter(item => Number(item.product_id) === Number(product.id));
        const existing = lines.find(item => item.properties?._careiz_upsell === 'true' && Number(item.properties?._careiz_main_product) === triggerId && (item.properties?._careiz_discount_code || '') === (rule.discount.code || ''));
        if (lines.length) return [];
        const variants = product.variants.filter(variant => variant.available);
        if (!variants.length && !existing) return [];
        const value = Number(rule.discount.value);
        const discounted = rule.discount.type !== 'none' && value > 0;
        const valid = Number.isFinite(value) && value >= 0 && (rule.discount.type !== 'percentage' || value <= 100);
        const configured = valid && (!discounted || ((rule.discount.type !== 'fixed_amount' || this.config.currency === 'ILS') && typeof rule.discount.code === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(rule.discount.code)));
        if (!configured && !this.config.editor) return [];
        seen.add(Number(product.id));
        const old = previous.get(Number(product.id));
        const variant = product.variants.find(variant => Number(variant.id) === Number(existing?.variant_id)) || variants.find(variant => Number(variant.id) === Number(old?.variant.id)) || variants[0];
        if (!variant) return [];
        return [{product,variants:variants.length ? variants : [variant],variant,offer:rule,mainId:triggerId,configured,selected:false,added:existing?.quantity || 0,actualPrice:existing ? Math.round(existing.final_line_price/existing.quantity) : undefined,busy:old?.busy || false,token:existing?.properties?._careiz_offer_key || old?.token || `${this.instance}-${product.id}`}];
      }).slice(0,this.config.maxOffers || 3);
      this.hidden = !this.entries.length;
      if (!this.entries.length) {
        this.content.replaceChildren();
        this.status.textContent = '';
        return;
      }
      this.render();
      this.hidden = false;
    }
    mainVariant() {
      const id = this.form?.querySelector('[name="id"]')?.value;
      return this.config.variants.find(variant => Number(variant.id) === Number(id)) || this.config.variants.find(variant => variant.available) || this.config.variants[0];
    }
    mainQuantity() { return Math.max(1,Number(this.form?.querySelector('[name="quantity"]')?.value || document.querySelector(`[name="quantity"][form="${this.form?.getAttribute('id')}"]`)?.value || 1)); }
    matchesVariant(rule,variantId) {
      const restriction = String(rule.triggerVariants || '').trim();
      return !restriction || restriction.split(/[\s,]+/).some(id => /^\d+$/.test(id) && Number(id) === Number(variantId));
    }
    price(entry) {
      if (entry.added > 0 && Number.isFinite(entry.actualPrice)) return entry.actualPrice;
      const base = Number(entry.variant.price);
      const discount = entry.offer.discount;
      if (discount?.type === 'percentage') return Math.max(0,Math.round(base * (1-Number(discount.value)/100)));
      if (discount?.type === 'fixed_amount') return Math.max(0,base-Math.round(Number(discount.value)*100));
      return base;
    }
    money(cents) { return new Intl.NumberFormat(this.config.locale || 'he-IL',{style:'currency',currency:this.config.currency}).format(cents/100).replace(/[\u061c\u200e\u200f]/g,''); }
    element(tag,className,text) {
      const element = document.createElement(tag);
      if (className) element.className = className;
      if (text !== undefined) element.textContent = text;
      return element;
    }
    emptyEditor() {
      if (this.config.context === 'cart') return;
      if (window.Shopify?.designMode) {
        this.content.textContent = this.config.labels.emptyEditor;
        this.hidden = false;
      }
    }
    render() {
      this.content.replaceChildren();
      if (this.preview) this.content.append(this.element('p','careiz-upsells__preview',this.config.labels.preview));
      this.content.append(this.element('h3','careiz-upsells__heading',this.settings.heading));
      if (this.settings.subheading) this.content.append(this.element('p','careiz-upsells__subheading',this.settings.subheading));
      if (this.settings.showMain) {
        const main = this.element('div','careiz-upsells__main');
        const image = this.element('img'); image.src = this.config.productImage; image.alt = ''; image.width = 48; image.height = 48;
        const text = this.element('div');
        text.append(this.element('strong','',`${this.config.labels.main}: ${this.config.productTitle}`),this.element('small','',this.config.labels.mainNote));
        main.append(image,text); this.content.append(main);
      }
      const list = this.element('div','careiz-upsells__list');
      this.entries.forEach((entry,index) => list.append(this.renderEntry(entry,index)));
      this.content.append(list);
      if (this.settings.showTotal) {
        const total = this.element('div','careiz-upsells__total');
        total.append(this.element('span','',this.config.labels.total));
        this.totalValue = this.element('strong');this.totalValue.dir = 'ltr';total.append(this.totalValue); this.content.append(total);
      }
      this.refreshMain();
    }
    renderEntry(entry,index) {
      const labels = this.config.labels;
      const visual = entry.offer;
      const titleText = visual?.title || entry.product.title;
      const row = this.element('div','careiz-upsells__item'); entry.row = row;
      const editorBlock = this.querySelector(`[data-careiz-block-id="${entry.offer.blockId}"]`);
      if (editorBlock) for (const attribute of editorBlock.attributes) {
        if (attribute.name.startsWith('data-shopify')) row.setAttribute(attribute.name,attribute.value);
      }
      if (this.settings.mode === 'checkbox') {
        const check = this.element('input','careiz-upsells__check');check.type = 'checkbox';check.id = `CareizOffer-${this.config.productId}-${this.dataset.sectionId}-${index}`;
        check.setAttribute('aria-label',`${labels.select} ${titleText}`);
        check.addEventListener('change',() => this.choose(entry,check.checked));entry.check = check;row.append(check);
      }
      const image = this.element('img','careiz-upsells__image');
      image.src = visual?.image || entry.variant.featured_image?.src || entry.product.featured_image;
      image.alt = titleText;image.width = 160;image.height = 160;image.loading = 'lazy';image.decoding = 'async';entry.image = image;row.append(image);
      const details = this.element('div','careiz-upsells__details');
      const title = this.element(this.settings.mode === 'checkbox' ? 'label' : 'span','careiz-upsells__title',titleText);
      title.dir = 'auto';
      if (entry.check) title.htmlFor = entry.check.id; details.append(title);
      const prices = this.element('div','careiz-upsells__prices');
      entry.after = this.element('bdi','careiz-upsells__price'); entry.after.setAttribute('aria-label',labels.after);
      entry.before = this.element('del','careiz-upsells__before');entry.before.setAttribute('aria-label',labels.before);
      entry.after.dir = 'ltr';entry.before.dir = 'ltr';
      prices.append(entry.after,entry.before);details.append(prices);
      if (!entry.configured) details.append(this.element('small','careiz-upsells__pending',this.config.labels.pendingDiscount));
      entry.saving = this.element('span','careiz-upsells__saving');details.append(entry.saving);
      if (entry.variants.length > 1) {
        const select = this.element('select','careiz-upsells__variant'); select.setAttribute('aria-label',`${labels.variant} ${titleText}`);
        entry.variants.forEach(variant => { const option = this.element('option','',variant.title);option.value = variant.id;select.append(option); });
        select.addEventListener('change',() => { entry.actualPrice = undefined;entry.variant = entry.variants.find(variant => Number(variant.id) === Number(select.value)); if (!visual?.image) image.src = entry.variant.featured_image?.src || entry.product.featured_image; this.updateEntry(entry); });
        entry.select = select;details.append(select);
      }
      const actions = this.element('div','careiz-upsells__actions');
      if (this.settings.mode === 'button') {
        entry.addButton = this.element('button','careiz-upsells__button',labels.add);entry.addButton.type = 'button';
        entry.addButton.addEventListener('click',() => this.enqueue(() => this.addOne(entry)));actions.append(entry.addButton);
      }
      entry.addedLabel = this.element('span','careiz-upsells__added',labels.added);entry.addedLabel.hidden = true;
      entry.removeButton = this.element('button','careiz-upsells__remove',labels.remove);entry.removeButton.type = 'button';entry.removeButton.hidden = true;
      entry.removeButton.addEventListener('click',() => this.enqueue(() => this.removeOne(entry)));
      actions.append(entry.addedLabel,entry.removeButton);details.append(actions);row.append(details);this.updateEntry(entry);return row;
    }
    updateEntry(entry) {
      const before = Number(entry.variant.price),after = this.price(entry),saving = before-after;
      entry.after.textContent = this.money(after);entry.before.textContent = this.money(before);entry.before.hidden = saving <= 0;
      const percent = before ? Math.round(saving*100/before) : 0;
      const savingsText = this.settings.savings === 'percent' ? `${percent}%` : this.settings.savings === 'both' ? `${this.money(saving)} · ${percent}%` : this.money(saving);
      entry.saving.textContent = this.settings.density === 'slim' ? savingsText : `${this.config.labels.save} ${savingsText}`;entry.saving.setAttribute('aria-label',`${this.config.labels.save} ${savingsText}`);entry.saving.hidden = saving <= 0 || this.settings.savings === 'none';
      entry.row.classList.toggle('is-selected',entry.selected);entry.row.classList.toggle('is-added',entry.added > 0);
      if (entry.check) { entry.check.checked = entry.selected || entry.added > 0;entry.check.disabled = entry.busy || !entry.configured; }
      if (entry.select) entry.select.disabled = entry.busy || entry.added > 0 || this.preview;
      if (entry.addButton) { entry.addButton.disabled = entry.busy || !entry.configured || this.preview;entry.addButton.hidden = entry.added > 0;entry.addButton.textContent = entry.busy ? this.config.labels.working : this.config.labels.add; }
      const staged = this.settings.mode === 'button' && entry.selected && entry.added === 0;
      if (entry.addButton && staged) entry.addButton.hidden = true;
      entry.addedLabel.textContent = staged ? this.config.labels.selected : this.config.labels.added;
      entry.addedLabel.hidden = entry.added === 0 && !staged;entry.removeButton.hidden = entry.added === 0 && !staged;entry.removeButton.disabled = entry.busy;
      this.refreshMain();
    }
    choose(entry,selected) {
      if (entry.busy || !entry.configured) return;
      if (this.config.context !== 'cart' && !this.matchesVariant(entry.offer,this.mainVariant()?.id)) return;
      if (entry.added > 0 && !selected) return this.enqueue(() => this.removeOne(entry));
      entry.selected = selected;this.updateEntry(entry);
    }
    refreshMain() {
      if (this.config.context !== 'cart') {
        for (const entry of this.entries) {
          const eligible = this.matchesVariant(entry.offer,this.mainVariant()?.id);
          if (!eligible) entry.selected = false;
          if (entry.row) {
            if (entry.row.hidden === eligible) entry.row.hidden = !eligible;
            entry.row.classList.toggle('is-selected',entry.selected);
          }
          if (entry.check) entry.check.checked = eligible && (entry.selected || entry.added > 0);
        }
        this.hidden = !this.entries.some(entry => this.matchesVariant(entry.offer,this.mainVariant()?.id));
      }
      if (!this.totalValue) return;
      const main = Number(this.mainVariant()?.price || 0)*this.mainQuantity();
      const extras = this.entries.reduce((sum,entry) => sum + (this.matchesVariant(entry.offer,this.mainVariant()?.id) && (entry.selected || entry.added > 0) ? this.price(entry)*Math.max(1,entry.added) : 0),0);
      const value = this.money(main+extras);
      if (this.totalValue.textContent !== value) this.totalValue.textContent = value;
    }
    item(entry) {
      return {id:Number(entry.variant.id),quantity:1,properties:{_careiz_upsell:'true',_careiz_main_product:String(entry.mainId || this.config.productId),_careiz_offer_key:entry.token,_careiz_discount_code:entry.offer.discount.code || '',_careiz_expected_price:String(this.price({...entry,added:0})),_careiz_currency:this.config.currency}};
    }
    selectedItems() {
      this.refreshMain();
      return this.entries.filter(entry => entry.selected && entry.configured && !entry.added && !entry.busy && this.matchesVariant(entry.offer,this.mainVariant()?.id)).map(entry => this.item(entry));
    }
    itemsAdded(response) {
      const returned = response.items || [response];
      this.entries.forEach(entry => {
        const matching = returned.find(item => item.properties?._careiz_offer_key === entry.token);
        if (matching) {entry.added += 1;entry.selected = false;this.updateEntry(entry);}
      });
    }
    enqueue(action) {
      this.cartQueue = this.cartQueue.then(action).catch(error => {this.feedback(error.message || this.config.labels.error);});
      return this.cartQueue;
    }
    feedback(message) {
      this.status.textContent = message;
      if (this.config.context === 'cart') document.dispatchEvent(new CustomEvent('careiz:cart-offer-feedback',{detail:{message}}));
    }
    async request(path,body) {
      const response = await fetch(`${this.root()}cart/${path}.js`,{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify(body)});
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(typeof data.description === 'string' ? data.description : this.config.labels.error);
      return data;
    }
    async getCart() {
      const response = await fetch(`${this.root()}cart.js`,{headers:{Accept:'application/json'}});
      if (!response.ok) throw new Error(this.config.labels.error);
      return response.json();
    }
    codesFrom(cart) {
      const codes = new Set((cart.discount_codes || []).map(discount => discount.code).filter(Boolean));
      const allocations = [
        ...(cart.cart_level_discount_applications || []),
        ...(cart.items || []).flatMap(item => (item.line_level_discount_allocations || []).map(allocation => allocation.discount_application))
      ];
      allocations.filter(Boolean).forEach(discount => {
        if (discount.type === 'discount_code' && discount.title) codes.add(discount.code || discount.title);
      });
      try { JSON.parse(cart.attributes?._careiz_applied_codes || '[]').forEach(code => codes.add(code)); } catch {}
      return [...codes];
    }
    async finalizeCart(addedResponse) {
      let cart = await this.getCart();
      if (window.CareizCart) {
        const result = await window.CareizCart.normalize({cart});cart = result.cart;
      }
      const nativeCodes = [...new Set(cart.items.filter(item => item.properties?._careiz_upsell === 'true').map(item => item.properties?._careiz_discount_code).filter(Boolean))];
      const previous = this.codesFrom(cart);
      const marketing = previous.filter(code => !code.toUpperCase().startsWith('CAREIZ-ADD-')).slice(0,1);
      const desired = [...new Set([...marketing,...nativeCodes])];
      if (desired.length > 5) {
        await this.removeUnpriced(cart);
        throw new Error(this.config.labels.tooManyCodes);
      }
      try {
        cart = await this.request('update',{discount:desired.join(','),attributes:{_careiz_applied_codes:JSON.stringify(nativeCodes)},...this.sectionRequest()});
      } catch (error) {
        await this.removeUnpriced(await this.getCart());
        throw error;
      }
      let rejected = false;
      for (const entry of this.entries.filter(entry => entry.added > 0)) {
        const line = cart.items.find(item => item.properties?._careiz_offer_key === entry.token);
        if (!line) {entry.added = 0;entry.selected = false;entry.actualPrice = undefined;rejected = true;this.updateEntry(entry);continue;}
        const actual = Math.round(Number(line.final_line_price ?? line.line_price)/line.quantity);
        const expected = this.price({...entry,added:0});
        if (actual > expected + 1) {
          await this.request('change',{id:line.key,quantity:Math.max(0,line.quantity-entry.added)});
          entry.added = 0;entry.selected = false;entry.actualPrice = undefined;rejected = true;
          cart = await this.getCart();
        } else entry.actualPrice = actual;
        this.updateEntry(entry);
      }
      if (rejected) {
        this.status.textContent = this.config.labels.discountError;
        const retainedCodes = [...new Set([...marketing,...cart.items.filter(item => item.properties?._careiz_upsell === 'true').map(item => item.properties?._careiz_discount_code).filter(Boolean)])];
        cart = await this.request('update',{discount:retainedCodes.join(','),attributes:{_careiz_applied_codes:JSON.stringify(retainedCodes.filter(code => code.toUpperCase().startsWith('CAREIZ-ADD-')))},...this.sectionRequest()});
      }
      this.cartWarning = rejected ? this.config.labels.discountError : '';
      return {...cart,id:addedResponse.id || addedResponse.items?.[0]?.id,sections:cart.sections || addedResponse.sections};
    }
    async removeUnpriced(cart) {
      for (const entry of this.entries.filter(entry => entry.added > 0)) {
        const line = cart.items.find(item => item.properties?._careiz_offer_key === entry.token);
        if (line) {await this.request('change',{id:line.key,quantity:Math.max(0,line.quantity-entry.added)});cart = await this.getCart();}
        entry.added = 0;entry.selected = false;entry.actualPrice = undefined;this.updateEntry(entry);
      }
    }
    cartElement() { return document.querySelector('cart-notification') || document.querySelector('cart-drawer'); }
    sectionRequest() {
      const cart = this.cartElement();
      const sections = cart ? cart.getSectionsToRender().map(section => section.id) : ['cart-icon-bubble'];
      if (this.config.context === 'cart') {
        for (const id of ['main-cart-items','main-cart-footer']) {
          const element = document.getElementById(id);
          if (element) sections.push(element.dataset.id || id);
        }
      }
      return {sections:[...new Set(sections)].slice(0,5),sections_url:window.location.pathname};
    }
    refreshCart(response) {
      const cart = this.cartElement();
      const onCartPage = this.config.context === 'cart' && document.querySelector('[data-careiz-cart-slot="page"]');
      if (onCartPage && response.sections) {
        for (const id of ['main-cart-items','main-cart-footer']) {
          const host = document.getElementById(id);
          const html = response.sections[host?.dataset.id || id];
          if (!host || !html) continue;
          const source = new DOMParser().parseFromString(html,'text/html').querySelector('.js-contents');
          const target = host.querySelector('.js-contents');
          if (source && target) target.innerHTML = source.innerHTML;
          host.classList.toggle('is-empty',response.item_count === 0);
          host.closest('cart-items')?.classList.toggle('is-empty',response.item_count === 0);
        }
        const bubble = document.getElementById('cart-icon-bubble');
        const html = response.sections['cart-icon-bubble'];
        if (bubble && html) bubble.innerHTML = new DOMParser().parseFromString(html,'text/html').querySelector('.shopify-section')?.innerHTML || bubble.innerHTML;
      } else if (cart && response.sections && cart.getSectionsToRender().every(section => response.sections[section.id])) {
        cart.classList.remove('is-empty');
        cart.renderContents({...response,id:response.id || response.items?.[0]?.id});
      }
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate,{source:'careiz-upsells',cartData:response});
    }
    async addOne(entry) {
      if (entry.busy || entry.added > 0 || !entry.configured || this.preview) return;
      if (this.config.context !== 'cart' && !this.matchesVariant(entry.offer,this.mainVariant()?.id)) return;
      entry.busy = true;this.feedback('');this.updateEntry(entry);
      try {
        if (this.config.context !== 'cart') {
          const current = await this.getCart();
          if (!current.items.some(item => Number(item.product_id) === Number(this.config.productId) && item.properties?._careiz_upsell !== 'true' && this.matchesVariant(entry.offer,item.variant_id || item.id))) {
            entry.selected = true;this.feedback(this.config.labels.pendingMain);return;
          }
        }
        if (this.config.context === 'cart') {
          const current = await this.getCart();
          const hasMain = current.items.some(item => Number(item.product_id) === Number(entry.mainId) && item.properties?._careiz_upsell !== 'true' && this.matchesVariant(entry.offer,item.variant_id || item.id));
          if (!hasMain || current.items.some(item => Number(item.product_id) === Number(entry.product.id))) {
            entry.busy = false;this.syncCart(current);return;
          }
        }
        this.cartElement()?.setActiveElement(document.activeElement);
        let response = await this.request('add',{items:[this.item(entry)],...this.sectionRequest()});
        this.itemsAdded(response);
        response = await this.finalizeCart(response);
        this.refreshCart(response);
        if (!entry.added) throw new Error(this.cartWarning || this.config.labels.error);
        this.feedback(this.config.labels.added);
      } finally {entry.busy = false;this.updateEntry(entry);}
    }
    async removeOne(entry) {
      if (entry.busy) return;
      if (!entry.added && entry.selected) {entry.selected = false;this.updateEntry(entry);return;}
      entry.busy = true;this.status.textContent = '';this.updateEntry(entry);
      try {
        const response = await fetch(`${this.root()}cart.js`,{headers:{Accept:'application/json'}});
        if (!response.ok) throw new Error(this.config.labels.error);
        const cart = await response.json();
        const line = cart.items.find(item => item.properties?._careiz_offer_key === entry.token);
        if (line) {
          const next = await this.request('change',{id:line.key,quantity:Math.max(0,line.quantity-entry.added),...this.sectionRequest()});
          this.refreshCart(next);
        }
        entry.added = 0;entry.selected = false;entry.actualPrice = undefined;
        this.refreshCart(await this.finalizeCart(await this.getCart()));
      } finally {entry.busy = false;this.updateEntry(entry);}
    }
  });
}




